import { useQuery } from '@tanstack/react-query';
import { getTosStatus } from '../services/tosApi';
import { tosKeys } from './queryKeys';
const MAX_TRANSIENT_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 500;
const RETRY_MAX_DELAY_MS = 4000;
/**
 * True for responses that will never succeed on retry — an unauthenticated or
 * forbidden tab. Retrying these just loops against a session that is gone.
 *
 * Anything else (network failure, timeout, 5xx) is transient and worth retrying:
 * those are exactly the blips that used to leave the composer silently dead.
 */
export function isTosAuthError(error) {
    var _a;
    const status = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status;
    return status === 401 || status === 403;
}
/**
 * Fetches the current user's TOS acceptance status from Rails.
 *
 * - Runs once on mount; re-runs after a successful `acceptTos` mutation
 *   (triggered by `useAcceptTosMutation` invalidating `tosKeys.status()`).
 * - `retry` — auth failures (401/403) stay terminal so an unauthenticated tab
 *   does not loop. Transient failures now retry with backoff; previously ALL
 *   failures were terminal, so one dropped request disabled the composer until
 *   the tab regained focus, with no error shown (see helpers/tosGate.ts).
 * - `staleTime: 5 min` — token TTL is 15 min; re-checking once per stale
 *   window is sufficient.
 *
 * Callers MUST distinguish `data === undefined` (unknown) from
 * `data.accepted === false` (known denial). Use `resolveTosGate`.
 */
export function useTosStatusQuery() {
    return useQuery({
        queryKey: tosKeys.status(),
        queryFn: getTosStatus,
        retry: (failureCount, error) => {
            if (isTosAuthError(error))
                return false;
            return failureCount < MAX_TRANSIENT_RETRIES;
        },
        retryDelay: (attemptIndex) => Math.min(RETRY_BASE_DELAY_MS * 2 ** attemptIndex, RETRY_MAX_DELAY_MS),
        staleTime: 5 * 60 * 1000
    });
}
